import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { videoExtensionsSet } from 'src/app/constants/video-extensions';
import { LotWatchRequestInspectionData } from 'src/app/services/auction-video-watch/types/lot-watch-request-inspection-data.type';
import SwiperCore, { Keyboard, Navigation, Pagination } from 'swiper';

import { AutoLotView } from '../../../const';
import {
  LotSource,
  LotTypeEnum,
  UserLotQuery,
} from '../../graphql/service/graphql-auto-main-service';
import { MediaItemIndexed } from './types/media-item-indexed.type';
import { MediaItem, MediaItemType } from './types/media-item.type';

SwiperCore.use([Pagination, Navigation, Keyboard]);

@Component({
  selector: 'app-lot-carousel',
  templateUrl: './lot-carousel.component.html',
  styleUrls: ['./lot-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotCarouselComponent implements OnChanges {
  @Input() lot: AutoLotView;
  @Input() lotType: LotTypeEnum;
  @Input() lotSource: LotSource;
  @Input() activeSlide = 0;
  @Input() isUsingSavedImages: boolean;
  @Input() currentUser?: UserLotQuery['currentUser'];
  @Input() inspectionData?: LotWatchRequestInspectionData;
  @Input() savedLot: UserLotQuery['lot'];

  @Output() activeSlideChange = new EventEmitter<number>();
  @Output() mediaChanged = new EventEmitter<void>();

  allMedia: MediaItemIndexed[] = [];
  regularMedia: MediaItemIndexed[] = [];
  inspectionMedia: MediaItemIndexed[] = [];

  get displayedInspectionMedia(): MediaItemIndexed[] {
    if (this.isLimited) {
      return this.inspectionMedia.slice(0, 6);
    }
    return this.inspectionMedia;
  }

  get isLimited(): boolean {
    return !!(this.inspectionData?.requestCompleted && !this.inspectionData?.isForCurrentUser);
  }

  activeSlideChanged(index: number): void {
    this.activeSlide = index;
    this.activeSlideChange.emit(index);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.lot || changes.inspectionData) {
      const regularMedia = this.buildRegularMedia();
      const inspectionMedia = this.buildInspectionMedia();
      const allMedia = [...regularMedia, ...inspectionMedia];

      this.reindexMedia(allMedia);
    }
  }

  reindexMedia(mediaItems: MediaItem[] | MediaItemIndexed[]): void {
    this.allMedia = mediaItems.map((media, index) => ({
      ...media,
      index,
    }));

    this.regularMedia = this.allMedia.filter((item) => item.type === 'regular');
    this.inspectionMedia = this.allMedia.filter((item) => item.type === 'inspection');
  }

  private buildRegularMedia(): MediaItem[] {
    const clearPhotos = this.currentUser?.hasClearPhotos ?? false;

    const images = this.lot.images || [];
    const thumbnails = this.lot.thumbnails || [];
    const videos = this.lot.jupiterLotData?.video || [];

    const imageMedia = images.map((url, index) => ({
      source: this.normalizeImageSource(url, clearPhotos),
      thumbSource: thumbnails[index]
        ? this.normalizeImageSource(thumbnails[index], clearPhotos)
        : undefined,
      mediaType: 'image' as const,
      type: MediaItemType.REGULAR,
    }));

    const videoMedia = videos.map((url) => ({
      source: url,
      mediaType: 'video' as const,
      type: MediaItemType.REGULAR,
    }));

    return [...imageMedia, ...videoMedia];
  }

  private normalizeImageSource(url: string, clearPhotos: boolean): string {
    if (clearPhotos) {
      return url.replace('/pp', '').replace('/qp', '');
    }

    return url;
  }

  private buildInspectionMedia(): MediaItem[] {
    const inspectionFiles = this.inspectionData?.inspectionFiles;

    if (!inspectionFiles) {
      return [];
    }

    const allFiles = [...(inspectionFiles.videos || []), ...(inspectionFiles.images || [])];

    return allFiles.map((url) => ({
      source: url,
      mediaType: this.getMediaType(url),
      type: MediaItemType.INSPECTION,
    }));
  }

  private getMediaType(url: string): MediaItem['mediaType'] {
    const extension = url.split('.').pop().toLowerCase();
    return videoExtensionsSet.has(extension) ? 'video' : 'image';
  }
}
